import React from 'react';
import componentProps, { ComponentProps } from '../../helpers/componentProps';
import { applyComponentTheme } from '../../theme';
import PageArrow from './PageArrow';
import PageNumber from './PageNumber';
import {
  Dots,
  PageNumberWrapper,
  PaginationGrid,
  PaginationWrapper,
  StyledIcon,
} from './styles';

export type PaginationProps = {
  totalPages: number;
  currentPage: number;
  setCurrentPage: (newPage: number, trackingLinkUsed: string) => void;
} & ComponentProps;

const Pagination: React.FC<PaginationProps> = ({
  totalPages,
  currentPage,
  setCurrentPage,
  ...props
}) => {
  const setPageNumber = (pageNumber: number, trackingLinkUsed: string): void => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber, trackingLinkUsed);
    }
  };

  return (
    <PaginationWrapper {...componentProps(props)}>
      <PaginationGrid desktop={7} tabletLandscape={9} tabletPortrait={7}>
        <PageArrow
          onClick={() => setPageNumber(currentPage - 1, 'Previous')}
          disabled={currentPage === 1}
          shownOnMobile
          variant="secondary">
          <StyledIcon name="chevron" flipX size="small" />
          <span>Previous</span>
        </PageArrow>
        <PageNumberWrapper>
          {/* First page is always shown on desktop but only on mobile when active page is 1 or 2 */}
          <PageNumber
            shownOnMobile={currentPage <= 2}
            disabled={currentPage === 1}
            key={1}
            onClick={() => setPageNumber(1, 'Number')}
            variant="secondary">
            {1}
          </PageNumber>
          {/* First dots are shown when active page is 3 or higher, never on mobile */}
          {currentPage > 3 && <Dots key="startDots">···</Dots>}
          {/* If active page is last page and there are more than 3 pages, the third to last page is shown */}
          {currentPage === totalPages && totalPages > 3 && (
            <PageNumber
              shownOnMobile
              key={currentPage - 2}
              onClick={() => setPageNumber(currentPage - 2, 'Number')}
              variant="secondary">
              {currentPage - 2}
            </PageNumber>
          )}
          {/* If on page 3 or higher show the previous page */}
          {currentPage > 2 && (
            <PageNumber
              shownOnMobile
              key={currentPage - 1}
              onClick={() => setPageNumber(currentPage - 1, 'Number')}
              variant="secondary">
              {currentPage - 1}
            </PageNumber>
          )}
          {/* If not on the first or last page add the current page */}
          {currentPage !== 1 && currentPage !== totalPages && (
            <PageNumber
              shownOnMobile
              disabled
              key={currentPage}
              onClick={() => setPageNumber(currentPage, 'Number')}
              variant="secondary">
              {currentPage}
            </PageNumber>
          )}
          {/* If the current page is not the penultimate or final page show the next page */}
          {currentPage < totalPages - 1 && (
            <PageNumber
              shownOnMobile
              key={currentPage + 1}
              onClick={() => setPageNumber(currentPage + 1, 'Number')}
              variant="secondary">
              {currentPage + 1}
            </PageNumber>
          )}
          {/* If the current page is the first and there are more than 3 pages total, show the 3rd page */}
          {currentPage === 1 && totalPages > 3 && (
            <PageNumber
              shownOnMobile
              key={currentPage + 2}
              onClick={() => setPageNumber(currentPage + 2, 'Number')}
              variant="secondary">
              {currentPage + 2}
            </PageNumber>
          )}
          {/* If the current page is not one of the final 3 pages show end dots */}
          {currentPage < totalPages - 2 && <Dots key="endDots">···</Dots>}
          {/* Unless there is only a single page, show the final page; on mobile only show if current page is 2 or less pages away from final page */}
          {totalPages !== 1 && (
            <PageNumber
              shownOnMobile={currentPage > totalPages - 2}
              disabled={currentPage === totalPages}
              key={totalPages}
              onClick={() => setPageNumber(totalPages, 'Number')}
              variant="secondary">
              {totalPages}
            </PageNumber>
          )}
        </PageNumberWrapper>
        <PageArrow
          onClick={() => setPageNumber(currentPage + 1, 'Next')}
          disabled={currentPage === totalPages}
          shownOnMobile
          isNextArrow
          variant="secondary">
          <span>Next</span>
          <StyledIcon name="chevron" size="small" />
        </PageArrow>
      </PaginationGrid>
    </PaginationWrapper>
  );
};

export default applyComponentTheme(Pagination, 'Pagination');
